// "Move to a workspace" in the "…" of one's own document (Me): a small window
// choosing the workspace and the space, saying what moving does, and then the
// document opens in that workspace. Put into ui-core's document screen through
// the shell's header actions (kind "page"); the offline edition has none.
//
// Promise:
// - Only one's own documents that can be written, and not indexes (an index
//   cannot move).
// - Offered are the spaces where one may write documents (page.write, the
//   document feature on, not archived) of the chosen workspace.
// - Moving is one way; the window says what it changes before it is done.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  DOCUMENT_FEATURE,
  PAGE_WRITE,
} from "@trameria/cloud-api/workspace-documents-shared";
import { m } from "@trameria/cloud-i18n";
import { m as core } from "@trameria/i18n";
import {
  apiErrorMessage,
  Button,
  ErrorLine,
  type HeaderAction,
  type HeaderActionProps,
  type HeaderActionTarget,
  Modal,
  type PageDTO,
  refetchDocument,
  SelectMenu,
  Skeleton,
  toast,
  useApi,
} from "@trameria/ui-core";
import { type ReactNode, useState } from "react";
import { spacesQuery, workspacesQuery, workspaceViewQuery } from "../workspace";
import { useReleaseDocument } from "./queries";

const NOTE = "text-[12.5px] leading-[1.7] text-muted";

/** One row of the window: what is chosen, and its choice. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center gap-3">
      <span className="w-[84px] flex-none text-[12.5px] text-muted">
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** What the document screen hands to its header actions (`{ page, writable }`). */
const documentOf = (ext: HeaderActionTarget["ext"]) =>
  ext as { page: PageDTO; writable: boolean } | undefined;

const movable = (doc: { page: PageDTO; writable: boolean } | undefined) =>
  doc !== undefined &&
  doc.page.ownerUserId !== null &&
  doc.writable &&
  doc.page.kind !== "index";

export const releaseAction: HeaderAction = {
  id: "release",
  menuItem: ({ ext }) =>
    movable(documentOf(ext)) ? { label: m.wd_release() } : null,
  Component: ReleaseHeader,
};

function ReleaseHeader({ ext, opened, close }: HeaderActionProps) {
  const doc = documentOf(ext);
  if (!opened || !doc || !movable(doc)) return null;
  const title = doc.page.title || core.documents_untitled();
  return (
    <Modal open onClose={close} label={m.wd_release_title({ title })}>
      <ReleaseBody page={doc.page} title={title} onClose={close} />
    </Modal>
  );
}

function ReleaseBody({
  page,
  title,
  onClose,
}: {
  page: PageDTO;
  title: string;
  onClose(): void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const workspaces = useQuery(workspacesQuery(api));
  const [picked, setPicked] = useState<string | null>(null);
  const [spacePicked, setSpacePicked] = useState<string | null>(null);
  const list = workspaces.data ?? [];
  const slug = picked ?? list[0]?.slug ?? null;
  const view = useQuery({
    ...workspaceViewQuery(api, slug ?? ""),
    enabled: slug !== null,
  });
  const spaces = useQuery({
    ...spacesQuery(api, slug ?? ""),
    enabled: slug !== null,
  });
  const canWrite =
    view.data !== undefined &&
    (view.data.me.isOwner || view.data.me.permissions.includes(PAGE_WRITE));
  const writable = canWrite
    ? (spaces.data ?? []).filter(
        (s) => s.archivedAt === null && s.features.includes(DOCUMENT_FEATURE),
      )
    : [];
  const space =
    writable.find((s) => s.id === spacePicked) ?? writable[0] ?? null;
  const release = useReleaseDocument();
  const loading =
    workspaces.isPending ||
    (slug !== null && (view.isPending || spaces.isPending));
  const move = () => {
    if (!slug || !space) return;
    release.mutate(
      { pageId: page.id, workspace: slug, spaceId: space.id },
      {
        onSuccess: async (place) => {
          // Its details say it is shared now, so it opens in the workspace.
          await refetchDocument(qc, api, page.key).catch(() => null);
          onClose();
          toast(m.wd_released({ title, workspace: place.workspace.name }));
          void navigate({
            to: `/${place.workspace.slug}/documents/${page.key}`,
          });
        },
      },
    );
  };
  return (
    <>
      <h2 className="text-[15px] font-normal text-fg">
        {m.wd_release_title({ title })}
      </h2>
      <p className={`mt-2 ${NOTE}`}>{m.wd_release_body()}</p>
      <div className="mt-4 flex flex-col gap-2">
        {loading ? (
          <Skeleton lines={[60, 40]} />
        ) : list.length === 0 ? (
          <p className={NOTE}>{m.wd_release_none()}</p>
        ) : (
          <>
            <Field label={m.wd_release_workspace()}>
              {list.length > 1 ? (
                <SelectMenu
                  label={m.wd_release_workspace()}
                  value={slug ?? ""}
                  options={list.map((w) => ({ value: w.slug, label: w.name }))}
                  onChange={(next) => {
                    setPicked(next);
                    setSpacePicked(null);
                  }}
                />
              ) : (
                <span className="text-[13px] text-body">{list[0]?.name}</span>
              )}
            </Field>
            <Field label={m.wd_release_space()}>
              {writable.length === 0 ? (
                <span className={NOTE}>{m.wd_release_no_space()}</span>
              ) : (
                <SelectMenu
                  label={m.wd_release_space()}
                  value={space?.id ?? ""}
                  options={writable.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  onChange={setSpacePicked}
                />
              )}
            </Field>
          </>
        )}
      </div>
      {release.isError && (
        <ErrorLine>
          {apiErrorMessage(release.error, m.wd_release_failed({ title }))}
        </ErrorLine>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {m.cv_cancel()}
        </Button>
        <Button
          variant="primary"
          disabled={!space || release.isPending}
          onClick={move}
        >
          {m.wd_release_action()}
        </Button>
      </div>
    </>
  );
}
